import { Slot } from '@radix-ui/react-slot';
import { ChevronRight, MoreHorizontal } from 'lucide-react';
import * as React from 'react';

import {
  DISPLAY,
  FLEX,
  FLEX_ALIGN,
  FLEX_WRAP,
  GAP_SCALE,
  FONT_WEIGHTS,
  COLOR_SCALE,
  OPACITY_SCALE,
  WORD_BREAK,
} from '@/components/design-system/tokens';
import { useTranslation } from '@/hooks/useTranslation';
import { cn } from '@/lib/utils';

function Breadcrumb({ ...props }: React.ComponentProps<'nav'>) {
  return <nav aria-label="breadcrumb" data-slot="breadcrumb" {...props} />;
}

function BreadcrumbList({ className, ...props }: React.ComponentProps<'ol'>) {
  return (
    <ol
      className={cn(
        WORD_BREAK['break-words'],
        DISPLAY.flex,
        FLEX_WRAP.wrap,
        FLEX_ALIGN.center,
        GAP_SCALE['1.5'],
        COLOR_SCALE['foreground-muted'],
        OPACITY_SCALE[80],
        'text-xs sm:gap-2.5',
        className
      )}
      data-slot="breadcrumb-list"
      {...props}
    />
  );
}

function BreadcrumbItem({ className, ...props }: React.ComponentProps<'li'>) {
  return (
    <li
      className={cn(DISPLAY.inlineFlex, FLEX.flexItemsCenter, GAP_SCALE['1.5'], className)}
      data-slot="breadcrumb-item"
      {...props}
    />
  );
}

interface BreadcrumbLinkProps extends React.ComponentPropsWithoutRef<'a'> {
  asChild?: boolean;
}

const BreadcrumbLink = React.forwardRef<HTMLAnchorElement, BreadcrumbLinkProps>(
  ({ className, asChild, children, ...props }, ref) => {
    if (asChild) {
      return (
        <Slot
          ref={ref}
          className={cn(
            'transition-colors',
            COLOR_SCALE['foreground-muted'],
            OPACITY_SCALE[80],
            'hover:text-foreground',
            className
          )}
          {...props}
        >
          {children}
        </Slot>
      );
    }

    return (
      <a
        ref={ref}
        className={cn(
          'transition-colors',
          COLOR_SCALE['foreground-muted'],
          OPACITY_SCALE[80],
          'hover:text-foreground',
          className
        )}
        data-slot="breadcrumb-link"
        {...props}
      >
        {children}
      </a>
    );
  }
);
BreadcrumbLink.displayName = 'BreadcrumbLink';

function BreadcrumbPage({ className, ...props }: React.ComponentProps<'span'>) {
  return (
    // biome-ignore lint(a11y/useFocusableInteractive): known
    <span
      aria-current="page"
      aria-disabled="true"
      className={cn(
        FONT_WEIGHTS.regular,
        COLOR_SCALE['foreground-muted'],
        OPACITY_SCALE[80],
        className
      )}
      data-slot="breadcrumb-page"
      role="link"
      {...props}
    />
  );
}

function BreadcrumbSeparator({ children, className, ...props }: React.ComponentProps<'li'>) {
  return (
    <li
      aria-hidden="true"
      className={cn('opacity-80 [&>svg]:size-4', className)}
      data-slot="breadcrumb-separator"
      role="presentation"
      {...props}
    >
      {children ?? <ChevronRight />}
    </li>
  );
}

function BreadcrumbEllipsis({ className, ...props }: React.ComponentProps<'span'>) {
  const { t } = useTranslation();

  return (
    <span
      aria-hidden="true"
      className={className}
      data-slot="breadcrumb-ellipsis"
      role="presentation"
      {...props}
    >
      <MoreHorizontal className="size-4" />
      <span className="sr-only">{t('common', 'more_label', 'More')}</span>
    </span>
  );
}

export {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
  BreadcrumbEllipsis,
};
